/**
 * A PAPÍR BILLENTYŰI A JELÖLŐKHÖZ — tiszta függvény, React nélkül (#64).
 *
 * Kétféle állapotban más a jelentésük. LETÉTEL KÖZBEN a nyilak a
 * célkeresztet viszik, az `Enter` letesz, az `Esc` elvet. Különben az `N`
 * indít, az `1–9` a jelölőre ugrik, az `E` a jegyzetét nyitja, az `Esc` a
 * kiemelést veszi le.
 *
 * A DÖNTÉS ITT VAN, A VÉGREHAJTÁS A NÉZŐBEN: a válasz egy művelet, vagy
 * `null` — akkor a billentyű a néző saját billentyűinek (nagyítás, lapozás)
 * és végül a böngészőnek megy tovább.
 */

import type { PaperNote } from './notes';

/** A nyilak lépése letételkor: a lap 1%-a, `Shift`-tel 5%-a. */
export const PIN_STEP = 0.01;
export const PIN_STEP_LARGE = 0.05;

export type NoteKeyAction =
  | { kind: 'nudge'; dx: number; dy: number }
  | { kind: 'commit' }
  | { kind: 'cancel' }
  | { kind: 'start' }
  | { kind: 'select'; key: string | null }
  | { kind: 'edit'; key: string }
  /** Lekezelve, de nincs teendő — tovább ne menjen. */
  | { kind: 'swallow' };

export interface KeyPress {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

export function noteKeyAction(
  press: KeyPress,
  placing: boolean,
  notes: { items: PaperNote[]; activeKey: string | null }
): NoteKeyAction | null {
  // A `Ctrl`/`Cmd` kombinációk a böngészőé maradnak, mint a néző többi billentyűjénél.
  if (press.ctrlKey || press.metaKey || press.altKey) {
    return null;
  }
  return placing ? placingKey(press) : idleKey(press, notes);
}

function placingKey(press: KeyPress): NoteKeyAction | null {
  const arrow = ARROWS[press.key];
  if (arrow !== undefined) {
    const step = press.shiftKey ? PIN_STEP_LARGE : PIN_STEP;
    return { kind: 'nudge', dx: arrow[0] * step, dy: arrow[1] * step };
  }
  if (press.key === 'Enter') {
    return { kind: 'commit' };
  }
  if (press.key === 'Escape') {
    return { kind: 'cancel' };
  }
  // LETÉTEL KÖZBEN IS a papíré az `1–9` — ugyanazért, mint alább. A `0`
  // (illesztés) átmegy: az a nézőé, és letétel közben is hasznos.
  if (/^[1-9]$/.test(press.key)) {
    return { kind: 'swallow' };
  }
  return null;
}

function idleKey(
  press: KeyPress,
  notes: { items: PaperNote[]; activeKey: string | null }
): NoteKeyAction | null {
  const key = press.key.toLowerCase();
  if (key === 'n') {
    return { kind: 'start' };
  }
  // AZ `1–9` A PAPÍRON MINDIG A JELÖLŐKÉ — akkor is, ha nincs annyi. Tovább
  // engedve a munkalista Tensoft-választója kapná el, és egy „5" a papíron
  // csendben kontírt választana.
  if (/^[1-9]$/.test(press.key)) {
    const item = notes.items.find((note) => note.number === Number(press.key));
    return item === undefined ? { kind: 'swallow' } : { kind: 'select', key: item.key };
  }
  const active = notes.items.find((note) => note.key === notes.activeKey);
  if (key === 'e' && active !== undefined) {
    return { kind: 'edit', key: active.key };
  }
  if (press.key === 'Escape' && notes.activeKey !== null) {
    return { kind: 'select', key: null };
  }
  return null;
}
